import React from 'react';
import { Hotel } from '../types/hotel';
import {
  MapPin,
  Phone,
  Mail,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Hotel as HotelIcon,
} from 'lucide-react';
import { MEDIA_URL } from '../config';
import { getAccommodationLabelById, getAmenityLabels } from '../utils/helper';



interface HotelCardProps {
  hotel: Hotel;
  onViewDetails?: (id: string) => void;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  showActions?: boolean;
  compact?: boolean;
}

const HotelCard: React.FC<HotelCardProps> = ({
  hotel,
  onViewDetails,
  onApprove,
  onReject,
  showActions = false,
  compact = false,
}) => {

  /* ---------- SAFE DATA NORMALIZATION ---------- */

  const headerImage =
    (hotel as any).primaryPhoto ||
    (hotel as any).image ||
    (hotel as any).photos?.[0];

  const title =
    (hotel as any).propertyName ||
    (hotel as any).propName ||
    'Unnamed Property';

  const contact =
    (hotel as any).ownerContact ||
    hotel.ownerPhone ||
    hotel.phone ||
    '-';

  const basePrice = (hotel as any).basePrice;

  /* ---------- FIX AMENITIES FORMAT ---------- */
  const amenities = getAmenityLabels(hotel.amenities);


  // let amenities: string[] = [];

  // if (Array.isArray(hotel.amenities)) {
  //   amenities = hotel.amenities;
  // }
  // else if (typeof hotel.amenities === "string") {
  //   amenities = hotel.amenities
  //     .replace(/["\\]/g, "")
  //     .split(",")
  //     .map(a => a.trim());
  // }


  /* ---------- STATUS BADGE ---------- */

  const getStatusBadge = () => {
    switch ((hotel as any).status) {
      case 2:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-medium">
            <CheckCircle size={12} />
            Approved
          </span>
        );

      case 1:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-warm-100 text-warm-700 border border-warm-300 rounded-full text-xs font-medium">
            <Clock size={12} />
            Pending
          </span>
        );

      case 3:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded-full text-xs font-medium">
            <XCircle size={12} />
            Rejected
          </span>
        );

      default:
        return null;
    }
  };

  /* ---------- COMPACT CARD ---------- */

  if (compact) {
    return (
      <div className="bg-white border border-beige-300 rounded-xl overflow-hidden hover:shadow-card-hover transition-shadow group">

        {headerImage ? (
          <div
            className="h-28 bg-cover bg-center"
            style={{ backgroundImage: `url(${MEDIA_URL}${headerImage})` }}
          />
        ) : (
          <div className="h-28 bg-beige-100 flex items-center justify-center">
            <HotelIcon size={24} className="text-beige-400" />
          </div>
        )}

        <div className="p-3.5">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <h3 className="font-semibold text-sage-900 text-sm truncate">
                {title}
              </h3>
              <p className="text-xs text-sage-500 mt-0.5">
                {(hotel as any).ownerName}
              </p>
            </div>
            {getStatusBadge()}
          </div>

          <div className="flex items-center gap-1.5 text-xs text-sage-500">
            <MapPin size={12} className="text-sage-400 flex-shrink-0" />
            <span className="truncate">
              {(hotel as any).city}, {(hotel as any).country}
            </span>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- FULL CARD ---------- */

  return (
    <div className="bg-white border border-beige-300 rounded-xl overflow-hidden hover:shadow-card-hover transition-shadow group">

      {/* IMAGE HEADER */}

      {headerImage ? (
        <div className="relative h-44 w-full overflow-hidden">

          <div
            className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-300"
            style={{ backgroundImage: `url(${MEDIA_URL}${headerImage})` }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-sage-900/60 via-transparent to-transparent" />

          <div className="absolute top-3 right-3">
            {getStatusBadge()}
          </div>

          {basePrice && (
            <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-lg">
              <span className="text-sm font-bold text-sage-900">
                Tzs {basePrice}
              </span>
              <span className="text-xs text-sage-500">/night</span>
            </div>
          )}

        </div>
      ) : (
        <div className="relative bg-beige-50 px-5 py-4 border-b border-beige-200">
          <div className="flex items-start justify-between gap-3">

            <div className="flex items-center gap-3">

              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                <HotelIcon size={18} className="text-brand-500" />
              </div>

              <div>
                <h3 className="text-base font-semibold text-sage-900">
                  {title}
                </h3>
                <p className="text-xs text-sage-500 mt-0.5">
                  by {(hotel as any).ownerName}
                </p>
              </div>

            </div>

            {getStatusBadge()}

          </div>
        </div>
      )}

      {/* CONTENT */}

      <div className="p-5">

        {headerImage && (
          <div className="mb-3">
            <h3 className="text-base font-semibold text-sage-900">
              {title}
            </h3>
            <p className="text-xs text-sage-500 mt-0.5">
              by {(hotel as any).ownerName}
            </p>
          </div>
        )}

        {hotel.description && (
          <p className="text-sm text-sage-600 mb-4 line-clamp-2 leading-relaxed">
            {hotel.description}
          </p>
        )}

        {/* LOCATION */}

        <div className="grid grid-cols-2 gap-3 mb-4">

          <div>
            <p className="text-xs font-medium text-sage-400 uppercase tracking-wider mb-1">
              Location
            </p>

            <div className="flex items-center gap-1.5 text-sm text-sage-700">
              <MapPin size={13} className="text-sage-400 flex-shrink-0" />
              <span className="truncate">
                {(hotel as any).address}
              </span>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-sage-400 uppercase tracking-wider mb-1">
              Type
            </p>

            <span className="inline-block text-xs font-medium text-sage-600 bg-beige-100 px-2 py-1 rounded-md capitalize">
              {getAccommodationLabelById((hotel as any).accommodationType || 1)}
            </span>
          </div>

        </div>

        {/* CONTACT */}

        <div className="flex flex-col gap-1.5 mb-4 text-sm">

          <div className="flex items-center gap-2 text-sage-600">
            <Phone size={13} className="text-sage-400 flex-shrink-0" />
            <span className="text-xs">{contact}</span>
          </div>
          {hotel.email &&
            <div className="flex items-center gap-2 text-sage-600">
              <Mail size={13} className="text-sage-400 flex-shrink-0" />
              <span className="text-xs truncate">{hotel.email || '-'}</span>
            </div>
          }
        </div>

        {/* AMENITIES */}

        {amenities.length > 0 && (
          <div className="flex flex-wrap gap-1.5">

            {amenities.slice(0, 3).map((amenity, idx) => (
              <span
                key={idx}
                className="px-2 py-0.5 bg-brand-50 text-brand-600 rounded-md text-xs"
              >
                {amenity}
              </span>
            ))}

            {amenities.length > 3 && (
              <span className="px-2 py-0.5 bg-beige-100 text-sage-500 rounded-md text-xs font-medium">
                +{amenities.length - 3}
              </span>
            )}

          </div>
        )}

      </div>

      {/* ACTIONS */}

      {showActions && (onViewDetails || onApprove || onReject) && (

        <div className="border-t border-beige-200 px-5 py-3 flex items-center gap-2 bg-beige-50/50">

          {onViewDetails && (
            <button
              onClick={() => onViewDetails((hotel as any).id)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors"
            >
              <Eye size={14} />
              Details
            </button>
          )}

          {hotel.status === 1 && onApprove && (
            <button
              onClick={() => onApprove((hotel as any).id)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
            >
              <CheckCircle size={14} />
              Approve
            </button>
          )}

          {hotel.status === 1 && onReject && (
            <button
              onClick={() => onReject((hotel as any).id)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
            >
              <XCircle size={14} />
              Reject
            </button>
          )}

        </div>

      )}

    </div>
  );
};

export default HotelCard;